/**
 * Module-level buffer for the audio chunks of the live recording session.
 *
 * Why this isn't a React ref (MDRS-233): the chunks used to live in a `useRef`
 * inside `useRecordingSession`, which is owned by the app-wide `RecordingHost`.
 * Anything that remounts the host — an auth re-validation on navigation, an MSAL
 * account-array identity change, a parent re-key — destroyed the ref and every
 * chunk with it. The recording then ended with a null blob and no error surfaced:
 * the doctor's assessment was simply gone.
 *
 * Holding the chunks at module scope means the buffer outlives any component
 * tree, so a remount can no longer lose audio.
 *
 * The trade-off this introduces, and how it's handled: a buffer that outlives the
 * tree could let a *later* session inherit bytes from an earlier one — the stale-
 * audio-from-a-different-examinee hazard that `onRecordingSessionStart` was added
 * to close (MDRS-29 review). So every chunk is tagged with the owning session id,
 * and reads that don't match the current owner are discarded rather than reused.
 * Ownership is claimed once per `start()`, never inherited.
 */

/**
 * `sessionId` is set when a stopped session's durable copy was kept, so the
 * live instance can clear it once the recording is confirmed sent (MDRS-551).
 */
type FinalizeHandler = (
  blob: Blob | null,
  suspended?: boolean,
  sessionId?: string
) => void;

type RecordingBuffer = {
  /** Identifies the session that owns these chunks; null when empty. */
  sessionId: string | null;
  /** Case that owns the session. Diagnostic tag only — not a safety check. */
  caseNumber: string | null;
  chunks: Blob[];
  /** Mime type the MediaRecorder negotiated, tagged onto the assembled blob. */
  mimeType: string;
  /**
   * The live MediaRecorder for the owning session. Held here, not just in React
   * state, so a hook instance that mounts AFTER the session started can re-adopt
   * it and still stop/finalize the recording (MDRS-233). Without this the
   * session id was trapped in the dead hook's closure and the surviving audio
   * was unreachable — the recording still ended with a null blob.
   */
  recorder: MediaRecorder | null;
  /**
   * Whether some hook instance already has buffer-writing listeners bound to
   * `recorder`. Listeners live on the recorder object, not the closure, so they
   * keep firing after the registering component unmounts — a second set would
   * double-count chunks.
   */
  listenersAttached: boolean;
  /**
   * The currently-mounted hook instance's finalise handler. The recorder's own
   * `stop` listener belongs to whichever instance started the session, and after
   * a remount that instance's `setState` calls are no-ops — so the listener
   * reports the finished blob through here instead, and the LIVE instance
   * receives it.
   */
  onFinalize: FinalizeHandler | null;
  /**
   * Whether the owning session was discarded by the user.
   *
   * Lives on the buffer, not in a hook ref: a recorder's `stop` listener belongs
   * to whichever instance called `start()`, and after a remount that instance's
   * refs are unreachable. A per-instance flag therefore let a cancelled session
   * take the non-cancelled branch and dispatch a summarize for audio the user
   * had just thrown away (MDRS-233 review).
   */
  cancelled: boolean;
  // Engine failed: stop capture but keep the durable copy for Resume.
  suspended: boolean;
};

const buffer: RecordingBuffer = {
  sessionId: null,
  caseNumber: null,
  chunks: [],
  mimeType: "audio/webm",
  recorder: null,
  listenersAttached: false,
  onFinalize: null,
  cancelled: false,
  suspended: false,
};

/**
 * Marks the owning session as discarded. Any instance's listeners — including
 * those belonging to an unmounted one — see this and suppress finalisation.
 */
export const cancelRecordingBuffer = (): void => {
  buffer.cancelled = true;
};

/** Marks the owning session as suspended: its `stop` must neither summarize nor clear the durable copy. */
export const suspendRecordingBuffer = (): void => {
  buffer.suspended = true;
};

/** Whether the session that owns the buffer has been cancelled. */
export const isRecordingBufferCancelled = (): boolean => buffer.cancelled;

/**
 * Drops the captured audio AND releases ownership — for a MediaRecorder failure,
 * where the bytes are unusable but the session must still complete so the
 * transcript-only summarize path runs. Distinct from `cancelRecordingBuffer`,
 * which suppresses finalisation altogether.
 *
 * Ownership is released because the recorder stays in `recording` after an
 * `error` event (per spec), so it keeps emitting `dataavailable`. Leaving the id
 * claimed let those post-error chunks accumulate into a session already reported
 * as finished — bytes nobody would ever send, held at module scope until the next
 * `start()`. Releasing makes `appendRecordingChunk`'s id check reject them.
 */
export const discardRecordingAudio = (): void => {
  resetRecordingBuffer();
};

/**
 * Registers the mounted hook instance that should be told when the session
 * finalises. Later registrations win, so after a remount the live instance
 * receives the blob instead of the unmounted one that owns the listener.
 */
export const setRecordingFinalizeHandler = (
  handler: FinalizeHandler | null
): void => {
  buffer.onFinalize = handler;
};

/**
 * Deregisters a handler, but only if it is still the active one — a remount
 * registers the new instance's handler before the old instance's cleanup runs,
 * and that cleanup must not wipe its successor.
 */
export const clearRecordingFinalizeHandler = (handler: FinalizeHandler): void => {
  if (buffer.onFinalize === handler) buffer.onFinalize = null;
};

/**
 * Notifies the currently-registered instance that a session finalised.
 *
 * Callers MUST have checked `owned` from `finalizeRecordingBuffer` first. A
 * recorder can go inactive on its own (OS mic swap, unplugged headset — see the
 * `track.onended` path in Transcriber) and fire a LATE `stop` long after a newer
 * session started; notifying from that stale listener would flip the live
 * recording to `stopped` and dispatch a summarize with a null blob, orphaning
 * the in-progress audio (MDRS-233 review).
 */
export const notifyRecordingFinalized = (
  blob: Blob | null,
  suspended = false,
  sessionId?: string
): void => {
  buffer.onFinalize?.(blob, suspended, sessionId);
};

/**
 * Claims the buffer for a new session, discarding anything left behind. Returns
 * the session id the caller must present on every subsequent write/read.
 *
 * Ids are random rather than a counter so they stay unique across page reloads —
 * a stale id must never be able to address a fresh session's audio. Uniqueness
 * is what matters here, not unpredictability: the id never leaves the browser and
 * guards nothing but which in-memory chunks belong to which session.
 *
 * `crypto.randomUUID` unconditionally, with no weaker fallback: every browser
 * this app supports has it (the same `crypto` MSAL already relies on), and a
 * pseudo-random fallback only invites an "insecure randomness" reading.
 */
export const beginRecordingBuffer = ({
  caseNumber,
  mimeType,
  recorder,
}: {
  caseNumber: string | null;
  mimeType: string;
  recorder?: MediaRecorder | null;
}): string => {
  const sessionId = `rec-${crypto.randomUUID()}`;
  buffer.sessionId = sessionId;
  buffer.caseNumber = caseNumber;
  buffer.chunks = [];
  buffer.mimeType = mimeType;
  buffer.recorder = recorder ?? null;
  // `start` attaches listeners itself immediately after claiming.
  buffer.listenersAttached = true;
  return sessionId;
};

/**
 * Continues an interrupted recording after a reload: keeps its session id and
 * preloads the audio captured before, so finalise yields one blob.
 */
export const resumeRecordingBuffer = ({
  sessionId,
  caseNumber,
  mimeType,
  recorder,
  priorChunks,
}: {
  sessionId: string;
  caseNumber: string | null;
  mimeType: string;
  recorder?: MediaRecorder | null;
  priorChunks: Blob[];
}): string => {
  buffer.sessionId = sessionId;
  buffer.caseNumber = caseNumber;
  buffer.chunks = priorChunks.filter((chunk) => chunk && chunk.size > 0);
  buffer.mimeType = mimeType;
  buffer.recorder = recorder ?? null;
  buffer.listenersAttached = true;
  return sessionId;
};

/**
 * The in-flight session a newly-mounted hook should re-adopt, or null when no
 * recording is live. This is what makes buffered audio reachable across a
 * remount rather than merely present.
 */
export const adoptRecordingBuffer = (): {
  sessionId: string;
  recorder: MediaRecorder | null;
  caseNumber: string | null;
  /**
   * True when this adopter must wire its own listeners. Listeners are bound to
   * the MediaRecorder *object*, which outlives the closure that registered them,
   * so a re-adopted recorder is already writing to the buffer — attaching a
   * second set would count every subsequent chunk twice.
   */
  needsListeners: boolean;
  /**
   * The adopted recorder's live state ("recording" or "paused"), or null when
   * there's no recorder to adopt. The buffer already knows the recorder's
   * `.state` — reporting it here means the adopting hook doesn't need to
   * re-derive its own status from the recorder object as a second source of
   * truth.
   */
  status: "recording" | "paused" | null;
} | null => {
  if (!buffer.sessionId) return null;
  const needsListeners = !buffer.listenersAttached;
  buffer.listenersAttached = true;
  return {
    sessionId: buffer.sessionId,
    recorder: buffer.recorder,
    caseNumber: buffer.caseNumber,
    needsListeners,
    status: buffer.recorder
      ? buffer.recorder.state === "paused"
        ? "paused"
        : "recording"
      : null,
  };
};

/** Appends a chunk. Ignored unless `sessionId` still owns the buffer. */
export const appendRecordingChunk = (sessionId: string, chunk: Blob): void => {
  if (buffer.sessionId !== sessionId) return;
  if (!chunk || chunk.size === 0) return;
  buffer.chunks.push(chunk);
};

/**
 * Reads the buffered chunks. Pass a `sessionId` to assert ownership — a
 * mismatched id yields nothing, so a new session can never inherit stale audio.
 * Returns a copy: callers must not be able to mutate module state.
 *
 * Test-only inspection helper; production code finalises instead of reading.
 */
export const readRecordingBuffer = (sessionId?: string): Blob[] => {
  if (sessionId !== undefined && buffer.sessionId !== sessionId) return [];
  return [...buffer.chunks];
};

/**
 * The case that owns the buffered audio. Test-only inspection helper; the tag is
 * diagnostic-only — see the NOTE in `finalizeRecordingBuffer` for why finalisation
 * deliberately does not compare it.
 */
export const recordingBufferCaseNumber = (): string | null => buffer.caseNumber;

/**
 * Assembles the buffered chunks into a single blob and releases the buffer.
 * Returns null when the session no longer owns it or captured nothing.
 */
export const finalizeRecordingBuffer = (
  sessionId: string
): {
  owned: boolean;
  blob: Blob | null;
  cancelled: boolean;
  suspended?: boolean;
} => {
  // A stale session (its recorder fired `stop` late, after a newer session
  // claimed the buffer) must not finalise, and must not be reported as a
  // completion — that would end the recording the user is still making.
  if (buffer.sessionId !== sessionId) {
    return { owned: false, blob: null, cancelled: false };
  }
  if (buffer.cancelled) {
    resetRecordingBuffer();
    return { owned: true, blob: null, cancelled: true };
  }
  if (buffer.suspended) {
    resetRecordingBuffer();
    return { owned: true, blob: null, cancelled: false, suspended: true };
  }
  const { chunks, mimeType } = buffer;
  // NOTE: deliberately no cross-case discard here. The real hazard — a new
  // session inheriting a previous one's audio — is already closed by session-id
  // ownership: `beginRecordingBuffer` claims a fresh id per `start()` and never
  // inherits, so a mismatched id reads nothing. A guard comparing the owning
  // case at finalize time would instead destroy VALID audio, because the dock's
  // `caseNumber` is legitimately null for a tick after a remount. Losing a
  // doctor's recording is far worse than the case tag being diagnostic-only.
  const blob = chunks.length > 0 ? new Blob(chunks, { type: mimeType }) : null;
  resetRecordingBuffer();
  return { owned: true, blob, cancelled: false };
};

/**
 * Drops all buffered audio — used on cancel and between tests.
 *
 * Deliberately leaves `onFinalize` alone: it belongs to the mounted hook
 * instance, not to any one session, and clearing it here would silence the
 * next session's completion.
 */
export const resetRecordingBuffer = (): void => {
  buffer.sessionId = null;
  buffer.caseNumber = null;
  buffer.chunks = [];
  buffer.mimeType = "audio/webm";
  buffer.recorder = null;
  buffer.listenersAttached = false;
  buffer.cancelled = false;
  buffer.suspended = false;
};

/** Full teardown including the finalize handler — tests only. */
export const resetRecordingBufferForTest = (): void => {
  resetRecordingBuffer();
  buffer.onFinalize = null;
};
